/**
 * What the documents page's extraction card shows. A case can have several runs going at once:
 * every uploaded audio file is processed as a one-document run of its own, next to whatever
 * batch the user started. The card therefore follows the work that is still going, never "the
 * newest run": one running run is shown as the server reports it, several as their sum — the
 * card names no run and no document, only how many documents run, wait, are done and failed,
 * and its button leads to the list of runs. Pure.
 */
import type { ExtractionRun } from '@/services/api/cases'

const isRunning = (run: ExtractionRun) => run.status === 'running'

/**
 * How long a run keeps the runs read again. Nothing closes a run whose job is stuck — being
 * routed, or waiting for a worker that is gone — so it says "running" for good: left to itself
 * it would keep every open page asking every two seconds for ever. A day covers a long batch
 * and a queue of recordings behind one worker; a run older than that is shown still, and read
 * again when the page is.
 */
export const RUN_FOLLOWED_FOR_MS = 24 * 60 * 60 * 1000

/**
 * Whether the runs are read again: while a run is going that was created within the last
 * {@link RUN_FOLLOWED_FOR_MS}. `now` is in ms, on the clock `created_at` is read by.
 */
export function anyRunToFollow(runs: readonly ExtractionRun[] | undefined, now: number): boolean {
  return (runs ?? []).some(
    run => isRunning(run) && now - Date.parse(run.created_at) < RUN_FOLLOWED_FOR_MS
  )
}

type DocumentCount =
  | 'total_documents'
  | 'completed_documents'
  | 'failed_documents'
  | 'running_documents'
  | 'pending_documents'

/**
 * The extraction in progress, or undefined when no run is going. `runs` come newest first, as
 * the API lists them. Of several running runs the document counts are added up and the progress
 * is worked out over the sum by the server's own rule; everything a sum cannot say is the
 * oldest run's, and the time left is not known.
 */
export function extractionInProgress(
  runs: readonly ExtractionRun[] | undefined
): ExtractionRun | undefined {
  const running = (runs ?? []).filter(isRunning)
  if (running.length <= 1) return running[0]
  const sum = (field: DocumentCount) => running.reduce((count, run) => count + run[field], 0)
  const total = sum('total_documents')
  const completed = sum('completed_documents')
  const failed = sum('failed_documents')
  return {
    ...running[running.length - 1],
    total_documents: total,
    completed_documents: completed,
    failed_documents: failed,
    running_documents: sum('running_documents'),
    pending_documents: sum('pending_documents'),
    progress_percent: total > 0 ? Math.round(((completed + failed) / total) * 100) : 0,
    eta_ms: null,
    eta_reason: null,
  }
}
